import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import { publications, publicationBodies } from "@/brand/content";
import { SiteHeader5 } from "@/components/layout/SiteHeader5";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { InteriorHero } from "@/components/common/InteriorHero";
import { ConsultationBanner } from "@/components/common/ConsultationBanner";

/**
 * InsightsIndexPage — full publications index.
 *
 * Route: /concept-e/insights
 *
 * Features a filter bar over publication types (All / White Paper /
 * Practice Note / Field Report / Guide) and a formal publications
 * shelf below.
 */
const filters = ["All", "White Paper", "Practice Note", "Field Report", "Guide"];

export default function InsightsIndexPage() {
  const [active, setActive] = useState("All");
  const filtered =
    active === "All"
      ? publications
      : publications.filter((p) => p.tag === active);

  return (
    <main
      data-testid="page-insights"
      className="min-h-screen"
      style={{
        background: C.surfaceLight2,
        color: C.textOnLight,
        fontFamily: "Inter, sans-serif",
      }}
    >
      <SiteHeader5 />

      <InteriorHero
        breadcrumb={[
          { label: "Faith 1st", href: "/concept-e" },
          { label: "Insights" },
        ]}
        eyebrow="Publications & Insights"
        title={
          <>
            Practice notes,
            <br />
            white papers,
            <br />
            and field reports.
          </>
        }
        intro="Structured publications for organizational buyers making sense of the sensitive people, culture, leadership, and conduct terrain most organizations navigate without shared vocabulary."
        meta={[
          ["Publications", String(publications.length).padStart(2, "0")],
          ["Cadence", "Roughly quarterly"],
        ]}
      />

      {/* Filter bar */}
      <section
        data-testid="insights-filters"
        style={{
          background: C.surfaceLight2,
          borderBottom: `1px solid ${C.borderSoft}`,
        }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-8 flex flex-wrap items-center gap-x-3 gap-y-3">
          <span
            className="font-inter text-[10.5px] uppercase font-semibold mr-3"
            style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
          >
            Filter
          </span>
          {filters.map((f) => {
            const isActive = f === active;
            return (
              <button
                key={f}
                onClick={() => setActive(f)}
                data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-inter text-[11px] uppercase font-semibold px-4 py-2 transition-colors"
                style={{
                  background: isActive ? C.bgDark : "transparent",
                  color: isActive ? C.textOnDark : C.inkOnLight,
                  border: `1px solid ${isActive ? C.bgDark : C.borderSoft}`,
                  letterSpacing: "0.20em",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = C.accentMetal;
                    e.currentTarget.style.color = C.accentMetal;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = C.borderSoft;
                    e.currentTarget.style.color = C.inkOnLight;
                  }
                }}
              >
                {f}
              </button>
            );
          })}
        </div>
      </section>

      {/* Publications list */}
      <section
        data-testid="insights-list"
        style={{ background: C.surfaceLight2 }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          {filtered.length === 0 ? (
            <p
              className="font-cormorant italic"
              style={{ color: C.inkOnLightSoft, fontSize: "1.15rem" }}
            >
              No publications in this category yet.
            </p>
          ) : (
            <ul>
              {filtered.map((p, i) => {
                const body = publicationBodies[i] || publicationBodies[0];
                return (
                  <li
                    key={p.title}
                    data-testid={`publication-row-${i}`}
                    style={{
                      borderTop: i === 0 ? `1px solid ${C.borderSoft}` : "none",
                      borderBottom: `1px solid ${C.borderSoft}`,
                    }}
                  >
                    <Link
                      to={`/concept-e/insights/${body.slug}`}
                      className="group grid grid-cols-12 gap-x-6 gap-y-3 py-10 lg:py-12 transition-colors"
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "rgba(168,138,87,0.05)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <div className="col-span-4 lg:col-span-2">
                        <p
                          className="font-inter text-[10.5px] uppercase font-bold mb-1.5"
                          style={{
                            color: C.accentMetal,
                            letterSpacing: "0.26em",
                          }}
                        >
                          {p.tag}
                        </p>
                        <p
                          className="font-inter text-[10.5px] uppercase font-semibold tabular-nums"
                          style={{
                            color: C.inkOnLightSoft,
                            letterSpacing: "0.22em",
                          }}
                        >
                          {p.date}
                        </p>
                      </div>
                      <div className="col-span-8 lg:col-span-7">
                        <h3
                          className="font-cormorant mb-4"
                          style={{
                            color: C.textOnLight,
                            fontSize: "clamp(1.4rem, 2vw, 1.75rem)",
                            fontWeight: 400,
                            lineHeight: 1.15,
                            letterSpacing: "-0.005em",
                          }}
                        >
                          {p.title}
                        </h3>
                        <p
                          className="font-inter leading-[1.65] max-w-[54ch]"
                          style={{
                            color: C.inkOnLight,
                            fontSize: "0.98rem",
                          }}
                        >
                          {p.kicker}
                        </p>
                      </div>
                      <div className="col-span-8 lg:col-span-2 lg:text-right flex flex-col justify-between gap-3">
                        <p
                          className="font-inter text-[11px] uppercase font-semibold"
                          style={{
                            color: C.inkOnLightSoft,
                            letterSpacing: "0.20em",
                          }}
                        >
                          {p.length}
                        </p>
                        <span
                          className="inline-flex items-center justify-end gap-1.5 font-inter text-[10.5px] uppercase font-semibold"
                          style={{
                            color: C.accentMetal,
                            letterSpacing: "0.24em",
                          }}
                        >
                          Read
                          <ArrowRight
                            size={11}
                            weight="bold"
                            className="group-hover:translate-x-1 transition-transform"
                          />
                        </span>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <ConsultationBanner
        title="Prefer a conversation over a paper?"
        body="Every publication we publish comes out of engagements Faith 1st actually held. If one of them touches your organization, we welcome the inquiry."
      />
      <SiteFooter
        stamp="Concept 05"
        labelSuffix="Insights"
        markSubtitle="Leadership, People & Culture"
        descriptor="Faith 1st is a Leadership, People & Culture Consulting firm helping mission-driven and values-led organizations navigate high-stakes people moments through consulting, training, coaching, and facilitation."
      />
    </main>
  );
}
